import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ErrorMessages } from '@app/shared/constants/constants';
import { Router } from '@angular/router';
import { Priority } from '@app/shared/constants/global.enum';
import { VendorService } from '@app/shared/services/vendor.service';
import { buildDoughnutChartConfig } from '@app/shared/utils/chartData';

@Component({
  selector: 'app-ops-top-vendors',
  templateUrl: './ops-top-vendors.component.html',
  styleUrl: './ops-top-vendors.component.scss',
})
export class OpsTopVendorsComponent implements OnInit {
  chartData: any;
  chartOptions: any;
  errorMessage: string | null = null;
  vendorDetails: any[] = [];
  vendorLegendItems: { label: string; color: string; value: number }[] = [];
  monthDropdownOptions: { value: string; label: string }[] = [];
  selectedOption: string = '';
  month: number = 0;
  year: number = 0;
  priority = Priority;
  isDataLoaded: boolean = false;
  errorMsg: string = ErrorMessages.NoDataAvailable;
  imageSrc: string = 'assets/img/no-data-found.png';

  constructor(
    private cdr: ChangeDetectorRef,
    private vendorService: VendorService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.generateMonthDropdownOptions();
    this.loadVendorDetails(this.month, this.year);
    this.selectedOption = this.monthDropdownOptions[0]?.value;
    this.cdr.detectChanges();
  }

  generateMonthDropdownOptions(): void {
    const currentDate = new Date();
    const monthsToShow = 12;
    const options = [];
    for (let i = 0; i < monthsToShow; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      const month = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      options.push({
        value: `${month} ${year}`,
        label: `${month}, ${year}`,
        selected: i === 0,
      });
    }
    this.monthDropdownOptions = options;
    this.selectedOption = this.monthDropdownOptions[0].value;
    this.setMonthAndYear(this.selectedOption);
  }

  private setMonthAndYear(selectedOption: string): void {
    const [monthValue, yearValue] = selectedOption.split(' ');
    const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
    const formattedMonth = monthStr < 10 ? `0${monthStr}` : `${monthStr}`;
    this.month = parseInt(formattedMonth);
    this.year = parseInt(yearValue);
  }

  private loadVendorDetails(month: number, year: number): void {
    this.vendorService.getOpsTopVendors(month, year).subscribe({
      next: (vendors) => {
        this.vendorDetails = Array.isArray(vendors.data) ? vendors.data : [];
        this.initializeChartData();
        this.isDataLoaded = true;
        this.errorMessage = this.vendorDetails.length === 0 ? ErrorMessages.NoDataAvailable : null;
      },
      error: (error) => {
        this.isDataLoaded = true;
        this.errorMessage = error?.message || 'No data available';
        this.vendorDetails = [];
        this.chartData = null;
      },
    });
  }

  private getChartColors(): string[] {
    const rootStyles = getComputedStyle(document.documentElement);
    const chartColors = rootStyles.getPropertyValue('--chart-colors').trim();
    return chartColors.split(',').map((color) => color.trim());
  }

  private initializeChartData(): void {
    const chartColors = this.getChartColors();
    const { chartData, chartOptions } = buildDoughnutChartConfig(
      this.vendorDetails,
      chartColors,
    );
    this.chartData = chartData;
    this.chartOptions = chartOptions;
    this.createVendorLegendItems(chartColors);
  }

  private createVendorLegendItems(colors: string[]): void {
    // Prefer chartData labels (mapped from employer field) for consistency
    const labels: string[] = Array.isArray(this.chartData?.labels) ? this.chartData.labels : [];
    const values: number[] = this.chartData?.datasets?.[0]?.data ?? [];
    this.vendorLegendItems = labels.map((label: string, index: number) => ({
      label: label || `Vendor ${index + 1}`,
      color: colors[index % colors.length],
      value: values[index] ?? 0
    }));
    // Fallback: if labels empty, attempt vendorDetails fields
    if (!this.vendorLegendItems.length) {
      this.vendorLegendItems = this.vendorDetails.map((vendor: any, index: number) => ({
        label: vendor.employer || vendor.vendorName || vendor.name || vendor.vendor || `Vendor ${index + 1}`,
        color: colors[index % colors.length],
        value: vendor.totalEmployees ?? 0
      }));
    }
  }

  onMonthChange(event: any): void {
    this.selectedOption = event;
    this.setMonthAndYear(this.selectedOption);
    this.loadVendorDetails(this.month, this.year);
  }

  navigateToVendorsList(){
    this.router.navigate(['ops-vendor-management/vendors-list'], {
      queryParams: {
        month: this.month,
        year: this.year
      }
    });
  }
}
